import type {
  IObjectViewModel,
  IImageSliceViewModel,
  IViewPortController,
  IViewPortOrientation,
  IViewPortManager,
  IViewPortManagerStrategy,
  IPickableRegistry,
  IPickable,
  IRaycastable,
  Intersection,
  IMeasurementViewModel,
  IRaycaster,
  ICameraInitializer,
  ICenterLineViewModel,
  ICameraController
} from '@/engine/app/interfaces';
import type {
  IUiThreeDPresentation,
  IPresentationChangeHandler,
  IUiThreeDPresentationContext,
  IUiTwoDPresentationContext
} from '@/engine/UIPresentation/Interfaces';
import type { IHandlerDescriptor, IPointerHandler } from './PointerController';
import { PointerController } from './PointerController';
import type * as THREE from 'three';
import { ObjectPickerController, PickObjectMouseAdapter, PickObjectTouchAdapter } from './PointerHandlerPickObject';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import type { IKeyboardHandler } from './KeyboardController';
import { KeyboardController } from './KeyboardController';
import type { ViewPortTooltipViewModel } from '@/engine/app/models/ViewPortTooltipViewModel';
import { CanvasAttributeObserver } from './CanvasAttributeObserver';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import type { IPickablesSelector } from './PickablesSelector';
import { PointerHandlerComposite } from '@/engine/app/modules/pointerStack/PointerHandlerComposite';

export class ViewPortManager implements IPresentationChangeHandler, IViewPortManager, IPickableRegistry, IRaycaster {
  private _strategy: IViewPortManagerStrategy;

  private readonly _viewportDescriptor: string;

  private readonly _viewportController: IViewPortController;

  private readonly _pointerController: PointerController;

  private readonly _keyboardController: KeyboardController;

  private readonly _clvm2PresentationMap: Map<ICenterLineViewModel, IUiThreeDPresentationContext> = new Map();

  private readonly _vm2PresentationMap: Map<IObjectViewModel, IUiThreeDPresentationContext> = new Map();

  private readonly _vm2TwoDPresentationMap: Map<IObjectViewModel, IUiTwoDPresentationContext> = new Map();

  private readonly _mvm2PresentationMap: Map<IMeasurementViewModel, IUiThreeDPresentationContext> = new Map();

  private readonly _mvm2TwoDPresentationMap: Map<IMeasurementViewModel, IUiTwoDPresentationContext> = new Map();

  private readonly _descriptor2PresentationMap: Map<string, IUiThreeDPresentationContext> = new Map();

  private readonly _vm2ControllerMap: Map<IMeasurementViewModel, IDisposable> = new Map();

  private readonly _vpTooltips: Array<ViewPortTooltipViewModel> = [];

  private readonly _imagePresentations: Array<IUiThreeDPresentationContext> = [];

  private readonly _helpersPresentations: Array<IUiThreeDPresentation> = [];

  private readonly _raycastables: Set<IRaycastable> = new Set();

  private readonly _pickables: Array<IPickable> = [];

  private readonly _defaultPickables: Array<IPickable>;

  private _pickingEnabled = true;

  private readonly _subscriptions: DisposablesBasket = new DisposablesBasket();

  private readonly _defaultCameraInitializer: ICameraInitializer;

  public constructor(
    strategy: IViewPortManagerStrategy,
    viewportDescriptor: string,
    viewportController: IViewPortController,
    loggerService: ObservabilityLoggerService,
    pickablesSelector: IPickablesSelector
  ) {
    this._strategy = strategy;

    this._strategy.setRaycasterProvider(this);

    this._defaultPickables = this._strategy.getDefaultPickables() || [];

    this._viewportDescriptor = viewportDescriptor;

    this._viewportController = viewportController;
    this._subscriptions.add(
      this._viewportController.getCameraController().onCameraModified(this.handleCameraChange.bind(this))
    );
    this._addCanvasAttributeObserver();

    this._defaultCameraInitializer = this._strategy.createDefaultCameraInitializer();

    const objectPickerController = new ObjectPickerController(
      this,
      loggerService,
      this._viewportDescriptor,
      pickablesSelector
    );

    const objectPickerHandler = new PointerHandlerComposite(objectPickerController, {
      mouseAdapter: new PickObjectMouseAdapter(),
      touchAdapter: new PickObjectTouchAdapter()
    });

    this._pointerController = new PointerController(
      this._viewportController,
      objectPickerHandler,
      this.getDescriptor()
    );
    this._strategy.createPointerHandlers(this._pointerController);

    this._keyboardController = new KeyboardController(this._viewportController);
    this._strategy.createKeyboardHandlers(this._keyboardController);

    this._addHelpersPresentations();
  }

  public dispose(): void {
    this._removeHelpersPresentations();
    this._subscriptions.dispose();
    this._strategy.dispose();
    this._pointerController.dispose();
    this._keyboardController.dispose();
    this.clearScene();
  }

  public getViewPortController(): IViewPortController {
    return this._viewportController;
  }

  private _addCanvasAttributeObserver(): void {
    const canvas = this._viewportController.getCameraController().getCanvas();

    this._subscriptions.add(
      new CanvasAttributeObserver(canvas, 'data-omit-helpers-presentations', (value: string | null): void => {
        const attributeIsPresent = value !== null;
        if (attributeIsPresent && value !== 'false') {
          this._removeHelpersPresentations();
        } else {
          this._addHelpersPresentations();
        }
      })
    );
  }

  private _addHelpersPresentations(): void {
    if (this._strategy.createViewHelpersPresentations && this._helpersPresentations.length === 0) {
      for (const presentation of this._strategy.createViewHelpersPresentations(this)) {
        this._helpersPresentations.push(presentation);
        this._viewportController.addSceneObject(presentation.get3dObject(), false);
      }
    }
  }

  private _removeHelpersPresentations(): void {
    this._helpersPresentations.forEach((presentation: IUiThreeDPresentation): void => {
      this._viewportController.removeSceneObject(presentation.get3dObject());
      presentation.dispose();
    });
    this._helpersPresentations.length = 0;
  }

  private _addRaycastable(object: IRaycastable): void {
    this._raycastables.add(object);
  }

  private _removeRaycastable(object: IRaycastable): void {
    this._raycastables.delete(object);
  }

  private _addPickable(object: IPickable, toTheBeginning = false): void {
    if (toTheBeginning) {
      this._pickables.unshift(object);
    } else {
      this._pickables.push(object);
    }
  }

  private _removePickable(object: IPickable): void {
    const i = this._pickables.indexOf(object);
    if (i >= 0) {
      this._pickables.splice(i, 1);
    }
  }

  public getPickables(): Array<IPickable> {
    return this._pickingEnabled ? this._pickables.concat(this._defaultPickables) : this._defaultPickables;
  }

  public setPickingPossibility(isPossible: boolean): void {
    this._pickingEnabled = isPossible;
  }

  public accessPresentation<T>(
    descriptor: string,
    accessor: (presentation: IUiThreeDPresentation | undefined) => T
  ): T {
    return accessor(this._descriptor2PresentationMap.get(descriptor)?.presentation);
  }

  public addPointerHandler(handler: IPointerHandler): IHandlerDescriptor {
    return this._pointerController.pushPointerHandler(handler);
  }

  private _addKeyboardHandler(handler: IKeyboardHandler): void {
    this._keyboardController.addKeyboardHandler(handler);
  }

  private _removeKeyboardHandler(handler: IKeyboardHandler): void {
    return this._keyboardController.removeKeyboardHandler(handler);
  }

  public setCenterLines(viewModels: Array<ICenterLineViewModel>): void {
    const currentlyAvailableVms = Array.from(this._clvm2PresentationMap.keys());

    currentlyAvailableVms.forEach((viewModel) => {
      if (!viewModels.includes(viewModel)) {
        return this._removeCenterLine(viewModel);
      }
    });

    viewModels.forEach((viewModel) => {
      if (!currentlyAvailableVms.includes(viewModel)) {
        return this._addCenterLine(viewModel);
      }
    });
  }

  public setObjects(viewModels: Array<IObjectViewModel>): void {
    const currentlyAvailableVms = Array.from(this._vm2PresentationMap.keys());

    currentlyAvailableVms.forEach((viewModel) => {
      if (!viewModels.includes(viewModel)) {
        return this._removeObject(viewModel);
      }
    });

    viewModels.forEach((viewModel) => {
      if (!currentlyAvailableVms.includes(viewModel)) {
        return this._addObject(viewModel);
      }
    });
  }

  public setMeasurements(viewModels: Array<IMeasurementViewModel>): void {
    const currentlyAvailableVms = Array.from(this._mvm2PresentationMap.keys());

    currentlyAvailableVms.forEach((viewModel) => {
      if (!viewModels.includes(viewModel)) {
        return this._removeMeasurement(viewModel);
      }
    });

    viewModels.forEach((viewModel) => {
      if (!currentlyAvailableVms.includes(viewModel)) {
        return this._addMeasurement(viewModel);
      }
    });
  }

  public setTooltips(viewModels: Array<ViewPortTooltipViewModel>): void {
    this._vpTooltips.forEach((viewModel) => {
      if (!viewModels.includes(viewModel)) {
        return this._removeTooltip(viewModel);
      }
    });

    viewModels.forEach((viewModel) => {
      if (!this._vpTooltips.includes(viewModel)) {
        return this._addTooltip(viewModel);
      }
    });
  }

  public fitCameraToBox(
    minCorner: THREE.Vector3,
    maxCorner: THREE.Vector3,
    orientationTop: THREE.Vector3,
    orientationDir: THREE.Vector3,
    fittingFactor: number
  ): void {
    this._defaultCameraInitializer.fitCameraToBox(minCorner, maxCorner, orientationTop, orientationDir, fittingFactor);
  }

  public getCameraController(): ICameraController {
    return this._viewportController.getCameraController();
  }

  private _addThreeDSceneObject(
    presentation: IUiThreeDPresentationContext,
    usedInFit: boolean,
    canBeClipped?: boolean
  ): void {
    this._viewportController.addSceneObject(
      presentation.presentation.get3dObject(),
      usedInFit,
      canBeClipped,
      usedInFit ? presentation.presentation.getGravityCenter?.() : undefined
    );
    if (presentation.raycastable) {
      this._addRaycastable(presentation.raycastable);
    }
    if (presentation.pickables) {
      for (const pickable of presentation.pickables) {
        this._addPickable(pickable.pickable, pickable.toTheBeginning);
      }
    }
    if (presentation.keyboardHandler) {
      this._addKeyboardHandler(presentation.keyboardHandler);
    }
  }

  private _removeThreeDSceneObject(presentation: IUiThreeDPresentationContext): void {
    this._viewportController.removeSceneObject(presentation.presentation.get3dObject());
    presentation.presentation.dispose();
    if (presentation.raycastable) {
      this._removeRaycastable(presentation.raycastable);
    }
    if (presentation.pickables) {
      for (const pickable of presentation.pickables) {
        this._removePickable(pickable.pickable);
      }
    }
    if (presentation.keyboardHandler) {
      this._removeKeyboardHandler(presentation.keyboardHandler);
    }
  }

  private _addTwoDSceneObject(presentation: IUiTwoDPresentationContext): void {
    this._viewportController.addTwoDObject(presentation.presentation.getTwoDViewModel());
    if (presentation.raycastable) {
      this._addRaycastable(presentation.raycastable);
    }
    if (presentation.pickables) {
      for (const pickable of presentation.pickables) {
        this._addPickable(pickable.pickable, pickable.toTheBeginning);
      }
    }
    if (presentation.keyboardHandler) {
      this._addKeyboardHandler(presentation.keyboardHandler);
    }
  }

  private _removeTwoDSceneObject(presentation: IUiTwoDPresentationContext): void {
    this._viewportController.removeTwoDObject(presentation.presentation.getTwoDViewModel());
    presentation.presentation.dispose();
    if (presentation.raycastable) {
      this._removeRaycastable(presentation.raycastable);
    }
    if (presentation.pickables) {
      for (const pickable of presentation.pickables) {
        this._removePickable(pickable.pickable);
      }
    }
    if (presentation.keyboardHandler) {
      this._removeKeyboardHandler(presentation.keyboardHandler);
    }
  }

  public createImageSlices(imageModels: Array<IImageSliceViewModel>): void {
    for (const imageModel of imageModels) {
      const presentation = this._strategy.createImagePresentation(imageModel, this);
      this._imagePresentations.push(presentation);
      this._addThreeDSceneObject(presentation, false);
    }
  }

  public getRaycaster(): IRaycaster {
    return this;
  }

  public rayCast(point: THREE.Vector2, predicate?: (intersection: Intersection) => boolean): Intersection | undefined {
    const intersections = this._viewportController.rayCast(this._raycastables, point);
    return predicate ? intersections.find(predicate) : intersections[0];
  }

  public getDescriptor(): string {
    return this._viewportDescriptor;
  }

  public setOrientation(orientation: IViewPortOrientation): void {
    this._viewportController.setupCamera(orientation);
  }

  public initCamera(): void {
    this._defaultCameraInitializer.initialize();
  }

  public handlePresentationChange(): void {
    this._viewportController.render();
  }

  public handleCameraChange(): void {
    this._mvm2TwoDPresentationMap.forEach((twoDPresentation) => {
      twoDPresentation.presentation.updateTwoDPosition();
    });
    this._vm2TwoDPresentationMap.forEach((twoDPresentation) => {
      twoDPresentation.presentation.updateTwoDPosition();
      twoDPresentation.presentation.updateLine?.();
    });
    this._viewportController.render();
  }

  public clearScene(): void {
    Array.from(this._vm2PresentationMap.keys()).forEach((viewModel) => {
      this._removeObject(viewModel);
    });
    Array.from(this._clvm2PresentationMap.keys()).forEach((viewModel) => {
      this._removeCenterLine(viewModel);
    });
    Array.from(this._mvm2PresentationMap.keys()).forEach((viewModel) => {
      this._removeMeasurement(viewModel);
    });
    this._vpTooltips.forEach((viewModel) => {
      this._removeTooltip(viewModel);
    });
    this._vm2PresentationMap.clear();
    this._clvm2PresentationMap.clear();
    this._vm2TwoDPresentationMap.clear();
    this._mvm2PresentationMap.clear();
    this._mvm2TwoDPresentationMap.clear();
    this._raycastables.clear();
    this._pickables.length = 0;
    this._defaultPickables.length = 0;

    this._imagePresentations.forEach((presentation: IUiThreeDPresentationContext) =>
      this._removeThreeDSceneObject(presentation)
    );
    this._imagePresentations.length = 0;

    this._defaultCameraInitializer.dispose();
  }

  private _addObject(viewModel: IObjectViewModel): void {
    const presentation = this._strategy.createThreeDObjectPresentation(viewModel, this);
    if (presentation) {
      this._descriptor2PresentationMap.set(viewModel.descriptor, presentation);
      this._vm2PresentationMap.set(viewModel, presentation);
      this._addThreeDSceneObject(presentation, true, viewModel.isClippable);
    }

    const twoDPresentation = this._strategy.createTwoDObjectPresentation(viewModel);
    if (twoDPresentation) {
      this._vm2TwoDPresentationMap.set(viewModel, twoDPresentation);
      this._addTwoDSceneObject(twoDPresentation);
    }
  }

  private _removeObject(viewModel: IObjectViewModel): void {
    const presentation = this._vm2PresentationMap.get(viewModel);
    if (presentation) {
      this._removeThreeDSceneObject(presentation);
      this._vm2PresentationMap.delete(viewModel);
      this._descriptor2PresentationMap.delete(viewModel.descriptor);
    }

    const twoDPresentation = this._vm2TwoDPresentationMap.get(viewModel);
    if (twoDPresentation) {
      this._removeTwoDSceneObject(twoDPresentation);
      this._vm2TwoDPresentationMap.delete(viewModel);
    }
  }

  private _addCenterLine(viewModel: ICenterLineViewModel): void {
    const presentation = this._strategy.createCenterLinePresentation(viewModel, this);
    this._descriptor2PresentationMap.set(viewModel.descriptor, presentation);
    this._clvm2PresentationMap.set(viewModel, presentation);
    this._addThreeDSceneObject(presentation, true);
  }

  private _removeCenterLine(viewModel: ICenterLineViewModel): void {
    const presentation = this._clvm2PresentationMap.get(viewModel);
    if (presentation) {
      this._removeThreeDSceneObject(presentation);
      this._clvm2PresentationMap.delete(viewModel);
      this._descriptor2PresentationMap.delete(viewModel.descriptor);
    }
  }

  private _addMeasurement(viewModel: IMeasurementViewModel): void {
    const presentation = this._strategy.createThreeDMeasurementPresentation(viewModel, this);
    if (presentation) {
      this._descriptor2PresentationMap.set(viewModel.descriptor, presentation);
      this._mvm2PresentationMap.set(viewModel, presentation);
      this._addThreeDSceneObject(presentation, false, viewModel.isClippable);
    }

    const twoDPresentation = this._strategy.createMeasurementTwoDPresentation(viewModel);
    if (twoDPresentation) {
      this._mvm2TwoDPresentationMap.set(viewModel, twoDPresentation);
      this._addTwoDSceneObject(twoDPresentation);
    }

    const measurementLabelPresentation = this._strategy.createMeasurementLabel(viewModel);
    if (measurementLabelPresentation) {
      this._vm2TwoDPresentationMap.set(viewModel, measurementLabelPresentation);
      this._addTwoDSceneObject(measurementLabelPresentation);
    }

    const dependencyController = this._strategy.createDependencyController(viewModel);
    if (dependencyController) {
      this._vm2ControllerMap.set(viewModel, dependencyController);
    }
  }

  private _removeMeasurement(viewModel: IMeasurementViewModel): void {
    const presentation = this._mvm2PresentationMap.get(viewModel);
    if (presentation) {
      this._removeThreeDSceneObject(presentation);
      this._mvm2PresentationMap.delete(viewModel);
      this._descriptor2PresentationMap.delete(viewModel.descriptor);
    }

    const twoDPresentation = this._mvm2TwoDPresentationMap.get(viewModel);
    if (twoDPresentation) {
      this._removeTwoDSceneObject(twoDPresentation);
      this._mvm2TwoDPresentationMap.delete(viewModel);
    }

    const measurementLabelPresentation = this._vm2TwoDPresentationMap.get(viewModel);
    if (measurementLabelPresentation) {
      this._removeTwoDSceneObject(measurementLabelPresentation);
      this._vm2TwoDPresentationMap.delete(viewModel);
    }

    const dependencyController = this._vm2ControllerMap.get(viewModel);
    if (dependencyController) {
      dependencyController.dispose();
      this._vm2ControllerMap.delete(viewModel);
    }
  }

  private _addTooltip(viewModel: ViewPortTooltipViewModel): void {
    this._vpTooltips.push(viewModel);
    this._viewportController.addTooltip(viewModel);
  }

  private _removeTooltip(viewModel: ViewPortTooltipViewModel): void {
    const index = this._vpTooltips.indexOf(viewModel);
    if (index > -1) {
      this._vpTooltips.splice(index, 1);
    }
    this._viewportController.removeTooltip(viewModel);
  }
}

